/**
 * The application navigation is an information architecture, not an
 * authorization system. `internal` only keeps the shell from advertising an
 * internal surface to an external subcontractor; App.js and the backend own
 * the actual boundaries.
 */
export const PRIMARY_NAVIGATION = [
  {
    id: "command-center",
    label: "Command Center",
    icon: "dashboard",
    children: [
      { path: "/dashboard", label: "Dashboard", icon: "dashboard" },
      { path: "/approvals", label: "Decisions", icon: "decisions", internal: true },
      { path: "/calendars", label: "Calendar", icon: "calendar-days" },
    ],
  },
  {
    id: "projects",
    label: "Projects",
    icon: "projects",
    path: "/projects",
  },
  {
    id: "pursuits",
    label: "Pursuits",
    icon: "pursuits",
    children: [
      { path: "/discover", label: "Find Work", icon: "discover", internal: true },
      { path: "/opportunities", label: "Pipeline", icon: "pipeline", internal: true },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: "operations",
    children: [
      { path: "/schedule", label: "Schedule", icon: "schedule" },
      { path: "/control-tower", label: "Control Tower", icon: "control-tower", internal: true },
      { path: "/procurement", label: "Procurement", icon: "procurement", internal: true },
      { path: "/marketplace", label: "Subcontractor Sourcing", icon: "marketplace" },
      { path: "/workforce", label: "Workforce", icon: "workforce" },
    ],
  },
  {
    id: "network",
    label: "Network",
    icon: "network",
    children: [
      { path: "/companies", label: "Companies", icon: "companies", internal: true },
      { path: "/contacts", label: "Contacts", icon: "contacts", internal: true },
    ],
  },
  {
    id: "communications",
    label: "Communications",
    icon: "communications",
    children: [
      { path: "/messages", label: "Messages", icon: "messages" },
      { path: "/email", label: "Email Campaigns", icon: "email", internal: true },
    ],
  },
];

export const UTILITY_NAVIGATION = [
  { id: "settings", path: "/settings", label: "Settings", icon: "settings" },
];

export const routeMatches = (pathname, path) =>
  pathname === path || (path !== "/dashboard" && pathname.startsWith(`${path}/`));

export const visiblePrimaryNavigation = (external) =>
  PRIMARY_NAVIGATION.map((entry) => {
    if (!entry.children) return entry;
    return {
      ...entry,
      children: entry.children.filter((child) => !(external && child.internal)),
    };
  }).filter((entry) => !entry.children || entry.children.length > 0);

export const activeNavigation = (pathname, primary = PRIMARY_NAVIGATION) => {
  if (pathname === "/") {
    return { groupId: "command-center", path: "/dashboard" };
  }
  for (const entry of primary) {
    if (entry.path && routeMatches(pathname, entry.path)) {
      return { groupId: entry.id, path: entry.path };
    }
    const child = entry.children?.find((candidate) => routeMatches(pathname, candidate.path));
    if (child) return { groupId: entry.id, path: child.path };
  }

  const utility = UTILITY_NAVIGATION.find((entry) => routeMatches(pathname, entry.path));
  return utility ? { groupId: utility.id, path: utility.path } : null;
};

export const navigationDestinations = () => [
  ...PRIMARY_NAVIGATION.flatMap((entry) =>
    entry.path ? [entry.path] : entry.children.map((child) => child.path)
  ),
  ...UTILITY_NAVIGATION.map((entry) => entry.path),
];
